import { useEffect, useState } from "react"
import { Button, Pagination, Table } from "react-bootstrap"
import { useDispatch, useSelector } from "react-redux"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { camelCaseToWords, downloadFile, formatDate, isGreaterOrLessThanSixtyDays } from "../../../constant/utils"
import ApplicantDetailModal from "../../Admin/ViewApplications/ApplicantDetailModal"
import { BinderModal } from "./BinderModal"
import { useNavigate } from "react-router-dom"
import { downloadQuotePDF, getDashboardList, renewApplication } from "../../../data/applicationSlice"
import Loader from "../../../components/Loader"
import { ApplicationThreadedComments } from "../../Admin/ViewApplications/ThreadedCommentsModal"
import { byPrefixAndName } from "@awesome.me/kit-de80aecb6a/icons"
import { css } from "@emotion/react"

const componentStyle = css`
    button:disabled {
        opacity: 0.2;
    }

    .notification-icon {
        color: #dc3545;
        animation: blink 1s infinite;
        font-size: 12px;
        position: relative;
    }

    @keyframes blink {
        0%,
        100% {
            opacity: 1;
        }
        50% {
            opacity: 0;
        }
    }

    .notification-container {
        position: relative;
        display: inline-block;
    }

    .notification-badge {
        position: absolute;
        top: 0;
        right: 0;
        transform: translate(50%, -50%);
    }
`

const ApplicationsAndQuotesTable = () => {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { dashboardData: applicationAndQuotes, fetchStatus, totalRecords } = useSelector((state) => state.application)

    const [showModal, setShowModal] = useState(false)
    const [showModalComments, setShowModalComments] = useState(false)
    const [showBinder, setSowBinder] = useState(false)
    const [applicationId, setApplicationId] = useState()
    const [activePage, setActivePage] = useState(0)
    const [hasNonReviewedComment, setHasNonReviewedComment] = useState(false)

    const numberOfRecordsPerPage = 25
    const totalPages = Math.ceil(totalRecords / numberOfRecordsPerPage)

    useEffect(() => {
        getApplicationsList()
    }, [])

    const handleContinueApp = (item) => {
        navigate("/agency/create-application", { state: { applicationId: item?.id, isRenewal: item?.renewal } })
    }

    const handleCloseModal = () => {
        setShowModal(false)
        setApplicationId(null)
    }
    const handleOpenModal = (id) => {
        setShowModal(true)
        setApplicationId(id)
    }

    const handleOpenModalComments = (id, comments) => {
        setShowModalComments(true)
        setApplicationId(id)
    }
    const handleCloseModalComments = () => {
        setShowModalComments(false)
        setApplicationId()
        if (hasNonReviewedComment) {
            getApplicationsList()
            setHasNonReviewedComment(false)
        }
    }

    const handleShowBinder = (id) => {
        setSowBinder(true)
        setApplicationId(id)
    }

    const getApplicationsList = (pageIndex = 1) => {
        return dispatch(
            getDashboardList({
                pageIndex,
                numberOfRecords: numberOfRecordsPerPage,
                orderBy: "firstNamedInsured",
                descending: true
            })
        )
    }

    // Function to handle page change
    const handlePageChange = (index) => {
        if (index >= 0) {
            if (index === activePage) return
            setActivePage(index)
            getApplicationsList(index + 1)
        }
    }

    const downloadApplicationQuote = (id) => {
        dispatch(downloadQuotePDF(id)).then(({ payload }) => {
            if (payload?.file) {
                downloadFile(payload)
            }
        })
    }

    const requestRenewal = (id) => {
        dispatch(renewApplication(id)).then(({ payload }) => {
            if (payload?.succeeded) {
                getApplicationsList()
            }
        })
    }

    const refetchApplications = () => {
        getApplicationsList()
    }

    const renderPagination = () => {
        const paginationItems = []
        const ellipsis = (
            <Pagination.Ellipsis
                key="ellipsis"
                disabled
            />
        )
        const pagesToShow = 5 // Number of pages to show in the middle
        const tailPagesToShow = 2 // Number of pages to show at the end

        // Add First and Previous Buttons
        paginationItems.push(
            <Pagination.First
                key="first"
                onClick={() => handlePageChange(0)}
                disabled={activePage === 0}
            />
        )
        paginationItems.push(
            <Pagination.Prev
                key="prev"
                onClick={() => handlePageChange(activePage - 1)}
                disabled={activePage === 0}
            />
        )

        // Add initial pages or ellipsis
        if (activePage > pagesToShow - 1) {
            paginationItems.push(
                <Pagination.Item
                    key={0}
                    onClick={() => handlePageChange(0)}
                    active={activePage === 0}>
                    {1}
                </Pagination.Item>
            )
            paginationItems.push(ellipsis)
        }

        // Add middle pages
        const startPage = Math.max(0, activePage - Math.floor(pagesToShow / 2))
        const endPage = Math.min(totalPages, startPage + pagesToShow)

        for (let i = startPage; i < endPage; i++) {
            paginationItems.push(
                <Pagination.Item
                    key={i}
                    onClick={() => handlePageChange(i)}
                    active={activePage === i}>
                    {i + 1}
                </Pagination.Item>
            )
        }

        // Add ending pages or ellipsis
        if (totalPages - activePage > pagesToShow) {
            paginationItems.push(ellipsis)
            for (let i = totalPages - tailPagesToShow; i < totalPages; i++) {
                paginationItems.push(
                    <Pagination.Item
                        key={i}
                        onClick={() => handlePageChange(i)}
                        active={activePage === i}>
                        {i + 1}
                    </Pagination.Item>
                )
            }
        }

        // Add Next and Last Buttons
        paginationItems.push(
            <Pagination.Next
                key="next"
                onClick={() => handlePageChange(activePage + 1)}
                disabled={activePage === totalPages - 1}
            />
        )
        paginationItems.push(
            <Pagination.Last
                key="last"
                onClick={() => handlePageChange(totalPages - 1)}
                disabled={activePage === totalPages - 1}
            />
        )

        return paginationItems
    }

    return (
        <div css={componentStyle}>
            <Loader show={fetchStatus === "loading"} />
            <ApplicantDetailModal
                showModal={showModal}
                applicationId={applicationId}
                handleCloseModal={handleCloseModal}
            />
            <ApplicationThreadedComments
                show={showModalComments}
                currentAppId={applicationId}
                hide={handleCloseModalComments}
                hasAdminView={false}
            />
            <BinderModal
                show={showBinder}
                hide={() => {
                    setSowBinder(false)
                }}
                applicationId={applicationId}
                refetchApplications={refetchApplications}
            />
            <div className="bg-white pt-3">
                <h5 className="ms-3">Applications and Quotes</h5>
                <Table
                    striped
                    bordered
                    hover
                    responsive
                    className="mt-2">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>#</th>
                            <th>First Named Insured</th>
                            <th>Date Started</th>
                            <th>Date Submitted</th>
                            <th>Status</th>
                            <th className="icon-column text-center">Renewal</th>
                            <th className="icon-column text-center">View Comments</th>
                            <th className="icon-column text-center">Continue Application</th>
                            <th className="icon-column text-center">View Application</th>
                            <th className="icon-column text-center">Quote PDF</th>
                            <th className="icon-column text-center">Bind Your Quote</th>
                        </tr>
                    </thead>
                    <tbody>
                        {applicationAndQuotes?.map((item, index) => {
                            return (
                                <tr key={index}>
                                    <td>{item?.friendlyId}</td>
                                    <td>{item?.customerNumber || ""}</td>
                                    <td>{item.firstNamedInsured}</td>
                                    <td>{formatDate(item.dateStarted)}</td>
                                    <td>{formatDate(item.dateSubmitted)}</td>
                                    <td>
                                        {item?.bindRequestDate && item?.status === "Approved" && item?.sepGaiCostSheetId
                                            ? "Bind Request Submitted"
                                            : camelCaseToWords(item.status)}
                                    </td>
                                    <td className="text-center">
                                        <Button
                                            onClick={() => requestRenewal(item.id)}
                                            variant="light"
                                            className="me-2 btn-sm"
                                            disabled={
                                                !item?.policyNumber ||
                                                !item?.mod ||
                                                !item?.dateExpiration ||
                                                !isGreaterOrLessThanSixtyDays(item?.dateExpiration)
                                            }>
                                            <FontAwesomeIcon icon={byPrefixAndName.fas["repeat"]} />
                                        </Button>
                                    </td>
                                    <td className="text-center">
                                        <div className="notification-container">
                                            <Button
                                                onClick={() => {
                                                    handleOpenModalComments(item.id)
                                                    if (item?.hasNonReviewedComment)
                                                        setHasNonReviewedComment(item.hasNonReviewedComment)
                                                }}
                                                variant="light"
                                                className="me-2 btn-sm">
                                                <FontAwesomeIcon icon={byPrefixAndName.fasl["comment"]} />
                                            </Button>
                                            {item?.hasNonReviewedComment && (
                                                <span className="notification-badge">
                                                    <FontAwesomeIcon
                                                        icon={byPrefixAndName.fasl["bell"]}
                                                        size="xs"
                                                        className="notification-icon mt-2"
                                                    />
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="text-center">
                                        <Button
                                            onClick={() => handleContinueApp(item)}
                                            variant="light"
                                            className="me-2 btn-sm"
                                            disabled={
                                                item?.status === "Approved" ||
                                                item?.status === "Under Review" ||
                                                item?.status === "BindRequestSubmitted"
                                            }>
                                            <FontAwesomeIcon icon={byPrefixAndName.far["arrow-right"]} />
                                        </Button>
                                    </td>
                                    <td className="text-center">
                                        <Button
                                            onClick={() => handleOpenModal(item?.id)}
                                            variant="light"
                                            className="me-2 btn-sm">
                                            <FontAwesomeIcon icon={byPrefixAndName.fal["magnifying-glass"]} />
                                        </Button>
                                    </td>
                                    <td className="text-center">
                                        <Button
                                            variant="light"
                                            className="me-2 btn-sm"
                                            onClick={() => downloadApplicationQuote(item?.id)}
                                            disabled={!(item?.status === "Approved")}>
                                            <FontAwesomeIcon icon={byPrefixAndName.fas["file-arrow-down"]} />
                                        </Button>
                                    </td>
                                    <td className="text-center">
                                        <Button
                                            disabled={
                                                !item?.sepGaiCostSheetId ||
                                                item?.status !== "Approved" ||
                                                item?.bindRequestDate
                                            }
                                            onClick={() => handleShowBinder(item?.id)}
                                            variant="light"
                                            className="me-2 btn-sm">
                                            Bind
                                        </Button>
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </Table>
                {totalPages > 1 && (
                    <Pagination
                        className="mt-4 d-flex justify-content-end"
                        size="md">
                        {renderPagination()}
                    </Pagination>
                )}
            </div>
        </div>
    )
}

export default ApplicationsAndQuotesTable
